// apps/storefront/src/routes/AuthRequiredGate.tsx
// —————————————————————————————————————————————————————————————————
// Guard "suave" para rutas que necesitan autenticación pero NO deben
// hacer redirect silencioso. Muestra un CTA de login inline en vez de
// expulsar al usuario.  Para rutas que SÍ deben redirigir (account,
// checkout), seguir usando ProtectedRoute.
// —————————————————————————————————————————————————————————————————

import { useLocation, Link } from "react-router-dom";
import type { ReactNode } from "react";
import { resolveStorefrontOrgSlug } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { LogIn } from "lucide-react";

function extractOrgSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/s\/([^/]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]).trim().toLowerCase() : null;
}

function buildStorefrontBasePath(orgSlug: string | null | undefined) {
  return orgSlug ? `/s/${orgSlug}` : "/";
}

type Props = {
  children: ReactNode;
  /** Título a mostrar cuando no hay sesión */
  title?: string;
  /** Descripción secundaria */
  description?: string;
};

export default function AuthRequiredGate({
  children,
  title = "Inicia sesión para continuar",
  description = "Esta sección requiere que estés identificado.",
}: Props) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);

  const loc = useLocation();
  const orgSlug =
    extractOrgSlugFromPath(loc.pathname) || resolveStorefrontOrgSlug();
  const basePath = buildStorefrontBasePath(orgSlug);

  if (!hydrated) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="rounded-2xl border border-zinc-800 bg-[var(--card)] px-5 py-4 text-sm opacity-80">
          Autenticando…
        </div>
      </div>
    );
  }

  if (accessToken && user) {
    return <>{children}</>;
  }

  // Guest mode → CTA inline
  const loginHref = `${basePath}/login?next=${encodeURIComponent(loc.pathname + loc.search)}`;
  const registerHref = `${basePath}/register?next=${encodeURIComponent(loc.pathname + loc.search)}`;

  return (
    <div className="container max-w-lg py-16">
      <div className="rounded-2xl border border-[rgb(var(--border-rgb))] bg-[var(--card)] p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[rgb(var(--primary-rgb)/0.1)]">
          <LogIn size={24} className="text-[var(--kw-primary-fg)]" />
        </div>

        <h2 className="text-lg font-semibold text-[rgb(var(--fg-rgb))]">
          {title}
        </h2>
        <p className="mt-1 text-sm text-[rgb(var(--fg-rgb)/0.7)]">
          {description}
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link
            to={loginHref}
            className="
              inline-flex items-center justify-center gap-2 rounded-xl
              bg-[rgb(var(--primary-rgb))] px-6 py-2.5
              text-sm font-medium text-[var(--kw-fg-on-primary)]
              hover:bg-[rgb(var(--primary-rgb)/0.9)]
              transition-colors
            "
          >
            Iniciar sesión
          </Link>
          <Link
            to={registerHref}
            className="
              inline-flex items-center justify-center gap-2 rounded-xl
              border border-[rgb(var(--border-rgb))] px-6 py-2.5
              text-sm font-medium text-[rgb(var(--fg-rgb))]
              hover:bg-[rgb(var(--muted-rgb))]
              transition-colors
            "
          >
            Crear cuenta
          </Link>
        </div>
      </div>
    </div>
  );
}
